import styled from '@emotion/styled'
import Head from 'next/head'
import { RefObject, memo, useEffect, useRef } from 'react'
import { openRoomState } from '../../../../actions/room'
import { modelGetSocialMedia, recoverFromKick } from '../../../../actions/user'
import { closeFloatingRoom } from '../../../../app-state/modals/state.floater'
import {
    destroyRoomState,
    updateRoomState,
} from '../../../../app-state/state.rooms'
import {
    FloaterSize,
    RoomView,
    SecureStateType,
    SettingsType,
} from '../../../../constants/enums'
import { enabledLounges } from '../../../../mfcLogic/lounge'
import { UserAccess } from '../../../../mfcLogic/userProperties/level'
import { idToSlug } from '../../../../state/state.users'
import i18n from '../../../../utilities/i18n'
import { AppStateType, useAppState } from '../../../Hooks/useAppState'
import { useHealthAppStateSelector } from '../../../Hooks/useHealthAppState'
import { useRoomSelector } from '../../../Hooks/useRoom'
import { useSecureState } from '../../../Hooks/useSecureState'
import { useSettingSelector } from '../../../Hooks/useSettings'
import { useUserSelector } from '../../../Hooks/useUser'
import { palette } from '../../../styles/colors'
import shape from '../../../styles/shape'
import Authentication, { AuthRoot } from '../../Authentication'
import AgeGateNotice from '../../Authentication/AgeGateNotice'
import FloatingPortal from '../../Chat/Rooms/FloatingRooms/FloatingPortal'
import CircularProgress from '../../CircularProgress'
import Empty from '../../Empty'
import { FloaterRegisterOptions, resolveIDs } from '../Floater'

const RoomRoot = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    height: '100%',
})
const StyledLoadingRoot = styled('section')({
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    height: '100%',
    width: '100%',
})

export type FloatingRoomProps = {
    id: number
    show: boolean
    isMobile: boolean
    vertical: boolean
    minimized: boolean
    width: number
    height: number
    size: FloaterSize
    roomView: RoomView
    ref: RefObject<FloaterRegisterOptions>
}

const Room = memo((props: FloatingRoomProps) => {
    const {
        id,
        size,
        width,
        height,
        show,
        isMobile,
        ref,
        vertical,
        minimized,
        roomView,
    } = props
    const isPm = roomView === RoomView.PM
    const isProfile = roomView === RoomView.Profile
    const { authenticated } = useAppState(AppStateType.APP)
    const rewardsPoints = useUserSelector('me', 'rewardsPoints')
    const level = useUserSelector('me', 'level')
    const { ageGateActive } = useSecureState(SecureStateType.AGE_VERIFICATION)
    const roomRef = useRef<HTMLDivElement>(undefined)
    const isLounge = useRoomSelector(id, 'isLounge')
    const surrogateID = useRoomSelector(id, 'loungeSurrogateID')
    const isOpen = useRoomSelector(id, 'isOpen')
    const { loungeSurrogateID, roomID } = resolveIDs(id, {
        isLounge,
        id,
        loungeSurrogateID: surrogateID,
    })
    // const width = location.width
    // const height = location.height
    const socketConnected = useHealthAppStateSelector('connected')
    const [enableFloatingPreview] = useSettingSelector(
        SettingsType.DEFAULT,
        'enableFloatingPreview',
    )
    const previousEnableFloatingPreview = useRef<boolean>(enableFloatingPreview)

    const foundId = isLounge ? loungeSurrogateID : roomID
    const userID = useUserSelector(roomID, 'id') // used for banned check
    const roomName = useUserSelector(roomID, 'name')
    const name = useUserSelector(foundId, 'name')
    const slug = useUserSelector(foundId, 'slug')
    const banned = useUserSelector(foundId, 'banned')
    const twitter = useUserSelector(foundId, 'twitter')
    const isKicked = useUserSelector(foundId, 'isKicked')
    const isOffline = useUserSelector(foundId, 'isOffline')
    const instagram = useUserSelector(foundId, 'instagram')
    const shareAlbums = useUserSelector(foundId, 'shareAlbums')
    const shareRecordings = useUserSelector(foundId, 'shareRecordings')
    const shareTipmenus = useUserSelector(foundId, 'shareTipmenus')
    const shareThings = useUserSelector(foundId, 'shareThings')

    const loaded = roomID && socketConnected
    const roomTitle = isLounge ? roomName : `${name}'s Room`
    const title = isPm
        ? `${i18n.t('messages.message.title')} ${roomName} - MyFreeCams App`
        : isProfile
          ? `${roomName} - MyFreeCams App`
          : `${roomTitle} - MyFreeCams App`

    const loungesWithAccess = enabledLounges(rewardsPoints, level)
    const hasSpecifiedLoungeAccess = loungesWithAccess?.includes(
        idToSlug[roomID] as any,
    )
    const canOpenRoom =
        !isProfile && // don't open room when just visiting profile
        (!isLounge || hasSpecifiedLoungeAccess) &&
        !banned &&
        !isKicked

    const alreadyOpen = !!(roomID === id && isOpen)
    const hasShare =
        shareAlbums + shareRecordings + shareTipmenus + shareThings > 0
    const socialMediaLoaded =
        typeof instagram === 'string' || typeof twitter === 'string'

    useEffect(() => {
        if (alreadyOpen || !canOpenRoom || !id) {
            return
        }
        openRoomState(id)
    }, [canOpenRoom, alreadyOpen, id])
    useEffect(() => {
        if (
            previousEnableFloatingPreview.current &&
            !enableFloatingPreview &&
            size < FloaterSize.Fullscreen
        ) {
            // hide the floating room if `enableFloatingPreview` changes
            // while the floating room preview is open
            updateRoomState(id, {
                viewing: false,
            })
        }
    }, [id, enableFloatingPreview, size])

    useEffect(() => {
        if (id && roomID && !isPm && !isProfile) {
            updateRoomState(id, {
                viewing: true,
            })
        }
        if (id) {
            return () => {
                recoverFromKick(id)
            }
        }
    }, [id, roomID, isPm, isProfile])

    useEffect(() => {
        if (banned || isKicked) {
            destroyRoomState(userID)
            if (size < FloaterSize.Fullscreen) {
                closeFloatingRoom(userID)
            }
        }
    }, [userID, banned, isKicked, size]) // cannot use `id` here as it will change before `banned` or `isKicked` do

    useEffect(() => {
        if (!isLounge && isOffline) {
            // if the model is offline, use the API to check if she has any MFC Share albums
            if (!hasShare && !socialMediaLoaded) {
                modelGetSocialMedia(slug)
            }
        }
    }, [isOffline, isLounge, slug])

    if (ageGateActive) {
        return <AgeGateNotice />
    }

    if (authenticated !== undefined && !authenticated && isPm) {
        return (
            <AuthRoot>
                <Authentication
                    subject={i18n.t('messages.sendPrivateMessages')}
                />
            </AuthRoot>
        )
    }

    // console.log(
    //     '[FloatingRoom]',
    //     { id, show },
    //     {
    //         // dataWidth,
    //         // dataHeight,
    //         locWidth: location.width,
    //         locHeight: location.height,
    //         // width,
    //         // height,
    //     },
    //     { canOpenRoom, alreadyOpen },
    //     // props,
    // )

    if (!id || !show) {
        return null
    }

    const RoomPortal = (
        <FloatingPortal
            loading={!loaded}
            size={size}
            width={width}
            height={height}
            vertical={vertical}
            isMobile={isMobile}
            ref={ref}
            hideVideo={minimized}
            roomView={roomView}
            id={id}
        />
    )
    return (
        <RoomRoot
            id="floating-room"
            ref={roomRef}
            data-testid={
                (size as number) < 3
                    ? 'FloatingRoom__Container__Preview'
                    : 'FloatingRoom__Container'
            }
            style={{
                borderRadius:
                    size === FloaterSize.Fullscreen ? 0 : shape.borderRadius,
                background:
                    !isLounge || (isLounge && hasSpecifiedLoungeAccess)
                        ? 'unset'
                        : palette.background,
            }}
        >
            {size === FloaterSize.Fullscreen && (
                <Head>
                    <title>{title}</title>
                </Head>
            )}
            {/* if isLounge */}
            {isLounge && (
                <>
                    {/* check for socket loaded & no app data points loaded */}
                    {!socketConnected && (
                        <StyledLoadingRoot>
                            <CircularProgress />
                        </StyledLoadingRoot>
                    )}
                    {/* socket loaded, no access */}
                    {!!socketConnected && !hasSpecifiedLoungeAccess && (
                        <Empty before={i18n.t('rooms.noLoungeAccess')} />
                    )}
                    {!!socketConnected &&
                        hasSpecifiedLoungeAccess &&
                        (rewardsPoints >= 0 ||
                            level > UserAccess.MEMBER_BASIC) &&
                        RoomPortal}
                </>
            )}
            {/* if not a isLounge, straight to SSR */}
            {!isLounge && RoomPortal}
        </RoomRoot>
    )
})

export default Room
